import classNames from "classnames";
import { ReactElement, HTMLAttributes, PropsWithChildren } from "react";
import useTranslation from "../../../utils/hooks/useTranslation";
import { Tooltip } from "antd";
import { SvgHelp } from "components/svg";

export type LabelPosition = "left";

export interface FormFieldProps {
  label?: string;
  className?: string;
  dataLength?: string;
  translateLabel?: boolean;
  translateUnit?: boolean;
  isAnt?: boolean;
  button?: ReactElement;
  icon?: any;
  labelPosition?: LabelPosition;
  borderLess?: boolean;
  error?: string;
  size?: string;
  info?: string;
  value?: any;
  helpText?: string;
  unit?: string;
  labelSize?: string | number;
  emptyClass?: boolean;
  placeholder?: string;
  minValue?: number;
  maxValue?: number;
}
export const FormField: React.FC<
  PropsWithChildren<FormFieldProps & HTMLAttributes<HTMLElement>>
> = ({
  icon,
  label,
  className,
  translateLabel = true,
  translateUnit = true,
  button,
  children,
  dataLength,
  isAnt,
  labelPosition,
  borderLess = false,
  error,
  size,
  value,
  info,
  helpText,
  unit,
  labelSize,
  emptyClass = true,
  placeholder,
  minValue,
  maxValue,
  ...rest
}) => {
  const { t } = useTranslation();
  const labelText = label && translateLabel ? t(label) : label;
  const unitText = unit && translateUnit ? t(unit) : unit;
  return (
    <material
      data-length={dataLength ?? "auto"}
      {...rest}
      className={classNames({
        empty: emptyClass && value !== 0 && !value && !placeholder,
        info_message: info,
        validationError: error,
      })}
      data-size={size === "large" ? "large" : ""}
    >
      <div className="field">
        <label className="form_fields">
          {!!(labelText || helpText) && (
            <div className="data_label">
              <text data-wrap="wrap" data-label-size={labelSize}>
                {labelText}
              </text>
              {helpText && (
                <Tooltip
                  arrow={false}
                  color="var(--accent-dark)"
                  space={false}
                  title={
                    <group data-direction="column" data-gap="10" data-space="5">
                      <text data-weight="700" data-wrap="wrap">
                        {helpText}
                      </text>
                    </group>
                  }
                >
                  <icon data-help="">
                    <SvgHelp />
                  </icon>
                </Tooltip>
              )}
            </div>
          )}
          <div className="field_cont">
            {children}
            {unitText && (
              <div className="marker" data-radius="less-special">
                <text>{unitText}</text>
              </div>
            )}
            {button}
            <i></i>
          </div>
        </label>
      </div>
      {info && (
        <infomessage>
          <text data-wrap="wrap" className="errorMessage">
            {info}
          </text>
        </infomessage>
      )}
      {error && (
        <errormessage>
          <text data-wrap="wrap" className="errorMessage">
            {t(error, [t(label || ""), minValue, maxValue])}
          </text>
        </errormessage>
      )}
    </material>
  );

  return (
    <div
      data-label={labelPosition}
      className={classNames("field", className, { third_part: isAnt })}
      data-length={dataLength === "autofit" ? "autofit" : ""}
      data-size={size}
      {...rest}
    >
      <label
        className={
          "form_fields " +
          classNames({
            validationError: error,
          })
        }
      >
        {!labelPosition && labelText && (
          <div className="data_label">
            <text data-wrap="wrap">{labelText}</text>
          </div>
        )}

        <div className="field_cont" data-length={dataLength}>
          {icon && (
            <>
              <icon data-space-horizontal="input" data-length="input">
                {icon}
              </icon>
              {!borderLess && (
                <separator data-vertical="" data-height="input"></separator>
              )}
            </>
          )}
          {labelPosition === "left" && labelText && (
            <div className="data_label">
              <text data-margin="input" data-ellipsis="">
                {labelText}
              </text>
              <separator data-vertical="" data-height="input"></separator>
            </div>
          )}

          {children}
          {button}

          {!borderLess && <i />}
        </div>
        {error ? <errormessage>{error}</errormessage> : null}
      </label>
    </div>
  );
};
